// CCBillUpgrade.tsx

"use client";

import { useGlobalContext } from "@/app/context/store";
import React, { useEffect, useRef, useState } from "react";
import { useSignInModal } from "../layout/sign-in-modal";
import Image from "next/image";
// import Link from "next/link";
// import { getLocalizedPath } from "../shared/formatURL";
import { IS_MAIN_SITE } from "../shared/sites/sites";
import { SubscribeGemsToggle } from "@/components/credits/subscribeGemsToggle";

interface MembershipOption {
  optionNumber: number;
  title: string;
  subtitle: string;
  priceInteger: string;
  priceDecimal: string;
  billingCycle: string;
  billedText: string;
  features: string[];
  ctaText: string;
  isAvailable: boolean;
  selectedBorderColor: string;
  bgColor: string;
  priceColor: string;
  membershipType: "SUBSCRIBESTAR" | "CCBILL";
  tierId?: string; // For SubscribeStar
  flexformId?: string; // For CCBILL
  paymentIcons: string[]; // Array of payment methods
}

const CCBillUpgrade = ({
  translations,
  signInModalTranslations,
  subGemsToggleTranslations,
}: {
  translations: any;
  signInModalTranslations: any;
  subGemsToggleTranslations: any;
}) => {
  const { user } = useGlobalContext();
  const { SignInModal, setShowSignInModal } = useSignInModal(signInModalTranslations);
  const [userSelected, setUserSelected] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(-1);
  const [clickCounter, setClickCounter] = useState(0);

  const selectMembership = (option: number) => {
    setUserSelected(true);
    setSelectedMembership(option);
    setClickCounter((prev) => prev + 1);
  };

  const membershipOptions: MembershipOption[] = [];

  if (IS_MAIN_SITE) {
    // Main site options
    membershipOptions.push(
      {
        optionNumber: 0,
        title: translations.AllAccessShort,
        subtitle: translations.AllAccessLong,
        priceInteger: "19",
        priceDecimal: "99",
        billingCycle: translations.Month,
        billedText: translations.BilledMonthSubstar,
        features: [translations.MonthlyCredits, translations.BulletOneSubstar], //`🎉 ${locale.MonthlyCreditsSale}`
        ctaText: translations.upgradeSubStar,
        isAvailable: true,
        selectedBorderColor: "border-[#FF6200]",
        bgColor: "bg-[#FF6200]",
        priceColor: "#FF6200",
        membershipType: "SUBSCRIBESTAR",
        tierId: "81998", //"107363",
        paymentIcons: ["visa", "mastercard"],
      },
      {
        optionNumber: 1,
        title: translations.YearShort,
        subtitle: translations.YearLong,
        priceInteger: "9",
        priceDecimal: "99",
        billingCycle: translations.Month,
        billedText: translations.BilledYear,
        features: [`${translations.MonthlyCredits}*`, translations.BulletOne],
        ctaText: translations.StartMembership,
        isAvailable: true,
        selectedBorderColor: "border-[#FFCD18]",
        bgColor: "bg-[#FBCA27]",
        priceColor: "#FBCA27",
        membershipType: "CCBILL",
        flexformId: "734b4485-b559-4d22-9134-c2a2526b0b75",
        paymentIcons: ["visa"],
      },
      {
        optionNumber: 2,
        title: translations.MonthShort,
        subtitle: translations.MonthLong,
        priceInteger: "14",
        priceDecimal: "99",
        billingCycle: translations.Month,
        billedText: translations.BilledMonth,
        features: [translations.MonthlyCredits, translations.BulletOne],
        ctaText: translations.StartMembership,
        isAvailable: true,
        selectedBorderColor: "border-[#FFCD18]",
        bgColor: "bg-[#FBCA27]",
        priceColor: "#FBCA27",
        membershipType: "CCBILL",
        flexformId: "84f8fb54-daa6-482f-b114-2b77073879c6",
        paymentIcons: ["visa"],
      },
      {
        optionNumber: 3,
        title: translations.TwoDayShort,
        subtitle: translations.TwoDayLong,
        priceInteger: "1",
        priceDecimal: "00",
        billingCycle: translations.TwoDays,
        billedText: translations.TrialBilled,
        features: [`${translations.MonthlyCredits}**`, translations.BulletOne],
        ctaText: translations.StartMembership,
        isAvailable: true,
        selectedBorderColor: "border-[#FFCD18]",
        bgColor: "bg-[#FBCA27]",
        priceColor: "#FBCA27",
        membershipType: "CCBILL",
        flexformId: "22923e09-5a53-4c78-9f77-cd8199b26466",
        paymentIcons: ["visa"],
      },
    );
  } else {
    // Non-main site options (all SubscribeStar)
    membershipOptions.push(
      {
        optionNumber: 0,
        title: translations.AllAccessShortLimited,
        subtitle: translations.AllAccessLongLimited,
        priceInteger: "19",
        priceDecimal: "99",
        billingCycle: translations.Month,
        billedText: translations.BilledMonthSubstar,
        features: [translations.MonthlyCredits, translations.BulletOneSubstar], //`🎉 ${locale.MonthlyCreditsSale}`
        ctaText: translations.upgradeSubStar,
        isAvailable: true,
        selectedBorderColor: "border-[#FF6200]",
        bgColor: "bg-[#FF6200]",
        priceColor: "#FF6200",
        membershipType: "SUBSCRIBESTAR",
        tierId: "81998", //"107363",
        paymentIcons: ["visa", "mastercard"],
      },
      {
        optionNumber: 1,
        title: translations.MonthShort,
        subtitle: translations.MonthLong,
        priceInteger: "14",
        priceDecimal: "99",
        billingCycle: translations.Month,
        billedText: translations.BilledMonth,
        features: [translations.MonthlyCredits, translations.BulletOneNiche],
        ctaText: translations.upgradeSubStar,
        isAvailable: true,
        selectedBorderColor: "border-[#FF6200]",
        bgColor: "bg-[#FF6200]",
        priceColor: "#FF6200",
        membershipType: "SUBSCRIBESTAR",
        tierId: "107361",
        paymentIcons: ["visa", "mastercard"],
      },
      {
        optionNumber: 2,
        title: translations.MonthShort,
        subtitle: translations.MonthLong,
        priceInteger: "9",
        priceDecimal: "99",
        billingCycle: translations.Month,
        billedText: translations.BilledMonthSubstarLimited,
        features: [translations.MonthlyCreditsStarter, translations.BulletOneNiche],
        ctaText: translations.upgradeSubStar,
        isAvailable: true,
        selectedBorderColor: "border-[#FF6200]",
        bgColor: "bg-[#FF6200]",
        priceColor: "#FF6200",
        membershipType: "SUBSCRIBESTAR",
        tierId: "107362",
        paymentIcons: ["visa", "mastercard"],
      },
    );
  }

  const selectedOption = membershipOptions.find(
    (option) => option.optionNumber === selectedMembership,
  );

  useEffect(() => {
    if (userSelected && selectedOption) {
      if (selectedOption.membershipType === "SUBSCRIBESTAR") {
        upgradeSubstarDiv.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      } else if (selectedOption.membershipType === "CCBILL") {
        upgradeDiv.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [selectedMembership, clickCounter, selectedOption]);

  const upgradeDiv = useRef<HTMLDivElement>(null);
  const upgradeSubstarDiv = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ref =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("ref") || ""
        : "";
    if (ref === "generator" && IS_MAIN_SITE) {
      setSelectedMembership(3);
    } else if (ref === "substar") {
      setSelectedMembership(0);
    } else {
      setSelectedMembership(IS_MAIN_SITE ? 1 : 0);
    }
  }, []);

  const handleStartMembership = () => {
    if (!selectedOption) {
      return;
    }

    if (selectedOption.membershipType === "SUBSCRIBESTAR") {
      // Handle SubscribeStar membership
      if (user.membership?.level === 1 && user.membership?.source === "SUBSCRIBESTAR") {
        alert("You already have a membership.");
      } else {
        const url = `https://subscribestar.adult/createaiart/subscribe?tier_id=${selectedOption.tierId}`;
        window.open(url, "_blank");
      }
    } else if (selectedOption.membershipType === "CCBILL") {
      // Handle CCBILL memberships
      if (user.temp || user._id === "none") {
        setShowSignInModal(true);
      } else {
        if (
          (user.membership?.level && user.membership?.source === "CCBILL") ||
          "CCBILL" in user.activeMemberships
        ) {
          alert("You already have a membership.");
        } else {
          const ref =
            typeof window !== "undefined"
              ? new URLSearchParams(window.location.search).get("ref") || ""
              : "";

          const url = `https://api.ccbill.com/wap-frontflex/flexforms/${
            selectedOption.flexformId
          }?userId=${user._id}${ref !== "" ? `&ref=${ref}` : ""}`;
          window.open(url, "_blank");
        }
      }
    }
  };

  const handleLinkAccount = async () => {
    if (user.temp || user._id === "none") {
      setShowSignInModal(true);
    } else {
      if (
        !(user.membership && user.membership.level === 1) ||
        user.membership?.source === "CCBILL"
      ) {
        const token = localStorage.getItem("jwtToken");
        if (token) {
          const url = `${process.env.NEXT_PUBLIC_BACKEND_URL}/subscribestar/connect?id=${token}&origin=${process.env.NEXT_PUBLIC_BASE_URL}`;
          window.open(url, "_blank");
        }
      }
    }
  };

  return (
    <>
      <SignInModal />
      <section
        className={`${IS_MAIN_SITE ? "w-full" : ""} z-0 mt-6 max-w-6xl px-2 sm:px-[20px] md:mt-8`}
      >
        <div className="mb-8">
          <SubscribeGemsToggle
            containerStyles="flex h-[42] w-full"
            labelStyles="mx-auto inline-flex cursor-pointer items-center rounded-lg bg-[#2B2D31] p-1.5"
            subscribeStyles="rounded-md bg-[#FFCC18] px-4 py-1 font-bold text-black"
            gemsStyles="px-4 py-1 font-bold text-[#FFCC18]"
            translations={subGemsToggleTranslations}
          />
        </div>

        <div
          className={`grid gap-2 sm:justify-items-center sm:gap-3 sm:space-y-0 md:justify-items-stretch md:gap-4 ${
            IS_MAIN_SITE ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-2 lg:grid-cols-3"
          }`}
        >
          {membershipOptions
            .filter((option) => option.isAvailable)
            .map((option) => (
              <>
                <div
                  key={option.optionNumber}
                  onClick={() => selectMembership(option.optionNumber)}
                  className={`mx-auto flex w-full max-w-lg flex-col rounded-xl
                  ${
                    selectedMembership === option.optionNumber
                      ? `border-4 ${option.selectedBorderColor}`
                      : "border-4 border-transparent"
                  } cursor-pointer bg-[#2B2D31] p-4 text-center text-white drop-shadow-2xl transition-colors duration-300 ease-in-out sm:p-4 md:p-6`}
                >
                  <div
                    className={`${option.optionNumber === 0 ? "bg-[#FF6200]" : "bg-[#FFCC18]"}
                                   ${
                                     (option.optionNumber === 0 || option.optionNumber === 1) &&
                                     IS_MAIN_SITE
                                       ? "mb-4"
                                       : option.optionNumber === 0 && !IS_MAIN_SITE
                                       ? "mb-4"
                                       : "invisible h-[8px] sm:h-auto"
                                   }
                                   ${
                                     !(
                                       (option.optionNumber === 0 || option.optionNumber === 1) &&
                                       IS_MAIN_SITE
                                     )
                                       ? "mb-0 sm:mb-4"
                                       : !(option.optionNumber === 0 && !IS_MAIN_SITE)
                                       ? "mb-0 sm:mb-4"
                                       : "invisible h-[8px] sm:h-auto"
                                   }
                                   mx-[-1rem] mt-[-1rem] rounded-b-none rounded-tl-md rounded-tr-md py-0.5 text-sm font-bold text-black md:mx-[-1.5rem] md:mt-[-1.5rem]`}
                  >
                    {option.optionNumber === 0
                      ? `✦ ${translations.mostPopular} ✦`
                      : option.optionNumber === 1
                      ? `✦ ${translations.mostValue} ✦`
                      : "x"}
                  </div>
                  <p
                    className="text-sm font-semibold sm:hidden"
                    style={{ color: option.priceColor }}
                  >
                    {option.title}
                  </p>
                  <p
                    className="hidden text-sm font-semibold sm:block sm:text-base"
                    style={{ color: option.priceColor }}
                  >
                    {option.subtitle}
                  </p>
                  <div className="my-4 flex items-end justify-center sm:my-5">
                    <div className="flex items-end font-bold italic">
                      <span className="relative text-lg sm:text-2xl" style={{ top: "-0.5em" }}>
                        $
                      </span>
                      <span className="text-4xl sm:text-5xl">{option.priceInteger}</span>
                      <span className="relative text-lg sm:text-2xl" style={{ top: "-0.5em" }}>
                        .{option.priceDecimal}
                      </span>
                      <span className="relative text-sm sm:text-base">/{option.billingCycle}</span>
                    </div>
                  </div>
                  <div className="mb-4 text-center text-[8.5px] text-white sm:mb-6 sm:text-[10px]">
                    <p>{option.billedText}</p>
                  </div>
                  <div className="flex justify-center">
                    <ul
                      role="list"
                      className="mb-2 space-y-2 text-left text-white sm:mb-3 sm:space-y-4"
                    >
                      {option.features.map((feature, index) => (
                        <li key={index} className="flex items-center space-x-3">
                          <svg
                            className="flex-shrink-1 h-5 w-5 text-green-500 dark:text-green-400"
                            fill={option.priceColor}
                            viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            ></path>
                          </svg>
                          <div className="flex flex-col">
                            <span
                              className={`sm:text-md pt-1 text-xs ${
                                index === 0 ? "font-bold" : ""
                              }`}
                            >
                              {feature}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-3 flex justify-center">
                    <div
                      className={`flex items-center justify-center rounded-full transition-colors duration-300 ease-in-out ${
                        selectedMembership === option.optionNumber
                          ? `${option.bgColor}`
                          : "bg-[gray]"
                      }`}
                      style={{ width: "120px", height: "40px" }}
                    >
                      <div>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 576 512"
                          className="h-5 w-5"
                          fill="black"
                        >
                          <path d="M0 24C0 10.7 10.7 0 24 0H69.5c22 0 41.5 12.8 50.6 32h411c26.3 0 45.5 25 38.6 50.4l-41 152.3c-8.5 31.4-37 53.3-69.5 53.3H170.7l5.4 28.5c2.2 11.3 12.1 19.5 23.6 19.5H488c13.3 0 24 10.7 24 24s-10.7 24-24 24H199.7c-34.6 0-64.3-24.6-70.7-58.5L77.4 54.5c-.7-3.8-4-6.5-7.9-6.5H24C10.7 48 0 37.3 0 24zM128 464a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zm336-48a48 48 0 1 1 0 96 48 48 0 1 1 0-96z" />
                        </svg>
                      </div>
                      {option.paymentIcons.includes("visa") && (
                        <Image
                          src={"https://cdn6.imgpog.com/visa.svg"}
                          width={27}
                          height={27}
                          alt="visa"
                          className="ml-4 mr-0"
                          unoptimized
                        />
                      )}
                      {option.paymentIcons.includes("mastercard") && (
                        <Image
                          src={"https://cdn6.imgpog.com/mastercard.svg"}
                          width={27}
                          height={27}
                          alt="mastercard"
                          className="ml-1 mr-0"
                          unoptimized
                        />
                      )}
                    </div>
                  </div>
                </div>
              </>
            ))}
        </div>
        {IS_MAIN_SITE && (
          <div className="mt-8 text-center text-[10px] text-gray-400">
            <p>*{translations.FineOne}</p>
            <p>**{translations.FineTwo}</p>
          </div>
        )}
        {selectedOption && selectedOption.membershipType === "SUBSCRIBESTAR" && (
          <>
            <div
              ref={upgradeSubstarDiv}
              className="mt-6 flex animate-fade-up flex-wrap justify-center gap-8 opacity-0"
              style={{
                animationDelay: "0.15s",
                animationFillMode: "forwards",
              }}
            >
              <div
                onClick={handleStartMembership}
                className="focus-visible:ring-ring ring-offset-background text-md max-w-xs transform cursor-pointer items-center justify-center rounded-full bg-[#FF6200] px-8 py-3 text-center font-black italic text-black shadow-2xl transition duration-500 ease-in-out hover:scale-105 hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 sm:max-w-sm sm:text-xl"
              >
                <p>1. {translations.upgradeSubStar}</p>
              </div>
              <div
                onClick={
                  user.membership?.source === "SUBSCRIBESTAR" ? undefined : handleLinkAccount
                }
                className={`my-auto ${
                  user.membership?.source === "SUBSCRIBESTAR"
                    ? "text-white"
                    : "sm:text-md cursor-pointer text-sm font-black text-[#FF6200] underline"
                } italic`}
              >
                <p>
                  {user.membership?.source === "SUBSCRIBESTAR"
                    ? translations.currentlySubscribed
                    : "2. " + translations.connectSubStar}
                </p>
              </div>
            </div>
            <div className="mt-10"></div>
          </>
        )}
        {selectedOption && selectedOption.membershipType === "CCBILL" && (
          <div ref={upgradeDiv}>
            <div
              onClick={handleStartMembership}
              style={{
                animationDelay: "0.15s",
                animationFillMode: "forwards",
              }}
              className="focus-visible:ring-ring ring-offset-background mx-auto mt-6 flex max-w-xs transform animate-fade-up cursor-pointer items-center justify-center rounded-full bg-[#FFCC18] px-8 py-3 text-center text-xl font-black italic text-black opacity-0 shadow-2xl transition duration-500 ease-in-out hover:scale-105 hover:bg-[#FF6200] hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 sm:max-w-sm"
            >
              <p>{translations.StartMembership}</p>
            </div>
            <div className="mt-4">
              <span className="opacity-0">.</span>
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default CCBillUpgrade;
